import React from "react";
import * as thoughtspot from "@thoughtspot/visual-embed-sdk";
import { getCustomizations } from "./customizations";
import { useThoughtSpotToken } from "./useThoughtSpotToken";
import * as Sentry from "@sentry/react";
import { ThoughtSpotStatus, useThoughtSpotStatusStore } from "./status";
import { AuthenticatedAPI, useAuthenticatedAPI } from "@/api";
import { useTheme } from "../hooks/useTheme";

const MAX_RETRIES = 5;

const tsInitSpan = Sentry.startInactiveSpan({ name: "thoughtpost initialization" });

export function useInitializeThoughtspot() {
  const saveToken = useThoughtSpotToken((state) => state.setToken);
  const { theme } = useTheme();
  const status = useThoughtSpotStatusStore((state) => state.status);
  const setStatus = useThoughtSpotStatusStore((state) => state.setStatus);

  const api = useAuthenticatedAPI();

  React.useEffect(() => {
    if (status !== ThoughtSpotStatus.Pending) {
      return;
    }

    return init(api, saveToken, theme, setStatus);
  }, [status, api, saveToken, theme, setStatus]);
}

let retries = 0;

function init(
  api: AuthenticatedAPI,
  saveToken: (token: string) => void,
  theme: "light" | "dark",
  setStatus: (status: ThoughtSpotStatus) => void,
) {
  return Sentry.startSpanManual(
    {
      name: "thoughtspot init try",
      parentSpan: tsInitSpan,
      attributes: {
        retryCount: retries,
      },
    },
    (span) => {
      let authStatus: ReturnType<typeof thoughtspot.init>;
      try {
        authStatus = thoughtspot.init({
          thoughtSpotHost: "https://norma.thoughtspot.cloud",
          authType: thoughtspot.AuthType.TrustedAuthTokenCookieless,
          getAuthToken: async () => {
            const { token } = await api.getThoughtspotToken();
            saveToken(token);
            return token;
          },
          customizations: getCustomizations(theme),
        });
      } catch (error) {
        span.addEvent("failure", { failureType: "init exception" });
        console.error("thoughtspot failure: ", error);
        maybeRetry();
        span.end();
        return;
      }

      if (!authStatus) {
        span.addEvent("failure", { failureType: "null init" });
        console.error("thoughtspot failure: ", "null init");
        maybeRetry();
        return;
      }

      function maybeRetry() {
        // keep the tsInitSpan active when retrying

        if (retries < MAX_RETRIES) {
          setStatus(ThoughtSpotStatus.Pending);
          retries++;
          init(api, saveToken, theme, setStatus);
        } else {
          setStatus(ThoughtSpotStatus.Failed);

          span.addEvent("max retries exceeded");
          tsInitSpan.end();
        }
      }

      function logoutHandler() {
        span.addEvent("logout");
        maybeRetry();
        span.end();
      }

      function failureHandler(failureType: thoughtspot.AuthFailureType) {
        span.addEvent("failure", { failureType });
        console.error("thoughtspot failure: ", failureType);
        maybeRetry();
        span.end();
      }

      function successHandler() {
        retries = 0;
        span.addEvent("success");
        console.log("thoughtspot success");
        setStatus(ThoughtSpotStatus.Authenticated);
        span.end();
        tsInitSpan.end();
      }

      authStatus.once(thoughtspot.AuthStatus.LOGOUT, logoutHandler);
      authStatus.once(thoughtspot.AuthStatus.FAILURE, failureHandler);
      authStatus.once(thoughtspot.AuthStatus.SDK_SUCCESS, successHandler);

      return () => {
        authStatus.off(thoughtspot.AuthStatus.LOGOUT, logoutHandler, undefined, true);
        authStatus.off(thoughtspot.AuthStatus.FAILURE, failureHandler, undefined, true);
        authStatus.off(thoughtspot.AuthStatus.SDK_SUCCESS, successHandler, undefined, true);
      };
    },
  );
}
